import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  formControlClass,
  formControlErrorClass,
  formErrorTextClass,
} from "../../../dashboardStyles";
import { DAY_KEYS, isTimeRangeValid } from "../utils/schedule";
import {
  MAX_TIME_OFF_DAYS,
  MAX_TIME_OFF_PERIODS,
  TIME_OFF_REASONS,
  daysIn,
  entriesOn,
  fromIsoDate,
  sameEntry,
  timeOffOf,
  toIsoDate,
  upcoming,
  withTimeOff,
} from "../utils/timeOff";

/** How far ahead the calendar pages: two years is past any booking horizon. */
const MAX_MONTHS_AHEAD = 24;

const REASON_STYLES = {
  dayOff: {
    day: "border-rose-300 bg-rose-100 text-rose-800",
    dot: "bg-rose-500",
  },
  break: { day: "border-sky-300 bg-sky-100 text-sky-900", dot: "bg-sky-500" },
};
const styleOf = (reason) => REASON_STYLES[reason] ?? REASON_STYLES.dayOff;

/** Whether the weekly hours have the resource working on this weekday. */
const worksOn = (resource, dayKey) =>
  resource?.useAdvancedSchedule
    ? Boolean(resource.advancedSchedule?.[dayKey]?.enabled)
    : Boolean(resource?.workingDays?.[dayKey]);

const chipClass = (active) =>
  `inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
    active
      ? "border-brand-600 bg-white text-brand-800 ring-1 ring-brand-600"
      : "border-ink-200 bg-white text-ink-600 hover:border-brand-300"
  }`;

/** Intl formatters in the reader's language, falling back when it is unknown. */
const useDateText = (language) => {
  const make = (options) => {
    try {
      return new Intl.DateTimeFormat(language, options);
    } catch {
      return new Intl.DateTimeFormat("ro", options);
    }
  };
  const full = make({ day: "numeric", month: "long", year: "numeric" });
  const long = make({
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const month = make({ month: "long", year: "numeric" });

  const capitalised = (text) => text.charAt(0).toUpperCase() + text.slice(1);
  return {
    monthTitle: (year, monthIndex) =>
      capitalised(month.format(new Date(year, monthIndex, 1))),
    dayName: (iso) => long.format(fromIsoDate(iso)),
    range: ({ start, end }) =>
      start === end
        ? full.format(fromIsoDate(start))
        : typeof full.formatRange === "function"
          ? full.formatRange(fromIsoDate(start), fromIsoDate(end))
          : `${full.format(fromIsoDate(start))} – ${full.format(fromIsoDate(end))}`,
  };
};

/**
 * Days off and breaks, on a month calendar.
 *
 * One press marks a day; a second press on another day turns it into the
 * period between the two — a holiday across a fortnight, or across the end of
 * a month after paging to the next one. The choice then gets a reason and,
 * for a break, the hours it covers on each of its days. Everything already
 * scheduled is coloured on the calendar and listed underneath to remove.
 *
 * Writes `monthlySchedule.timeOff` through `onChange`; the server takes these
 * away from the weekly hours when it offers slots (TimeOffPeriod.cs). Days the
 * weekly hours leave free are shown greyed but stay pressable, because a
 * holiday is usually entered as one stretch, weekend and all.
 */
const TimeOffEditor = ({ resource, onChange, idPrefix = "time-off" }) => {
  const { t, i18n } = useTranslation();
  const text = useDateText(i18n?.resolvedLanguage || i18n?.language || "ro");
  const todayIso = toIsoDate(new Date());
  const entries = timeOffOf(resource.monthlySchedule);

  const [view, setView] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [selection, setSelection] = useState(null);
  const [reason, setReason] = useState("dayOff");
  const [partOfDay, setPartOfDay] = useState(false);
  const [hours, setHours] = useState({ from: "12:00", to: "13:00" });

  const now = new Date();
  const monthsAhead =
    (view.year - now.getFullYear()) * 12 + view.month - now.getMonth();
  const page = (step) =>
    setView(({ year, month }) => {
      const date = new Date(year, month + step, 1);
      return { year: date.getFullYear(), month: date.getMonth() };
    });

  const choose = (iso) => {
    let next;
    if (!selection || selection.start !== selection.end) {
      next = { start: iso, end: iso };
    } else if (iso === selection.start) {
      next = null;
    } else {
      next =
        iso < selection.start
          ? { start: iso, end: selection.start }
          : { start: selection.start, end: iso };
    }
    setSelection(next);
  };

  const chooseReason = (value) => {
    setReason(value);
    setPartOfDay(value === "break");
  };

  const tooLong = selection && daysIn(selection) > MAX_TIME_OFF_DAYS;
  const atCap = entries.length >= MAX_TIME_OFF_PERIODS;
  const hoursInvalid = partOfDay && !isTimeRangeValid(hours.from, hours.to);
  const canAdd = Boolean(selection) && !tooLong && !atCap && !hoursInvalid;

  const add = () => {
    if (!canAdd) return;
    onChange(
      withTimeOff(resource.monthlySchedule, [
        ...entries,
        {
          start: selection.start,
          end: selection.end,
          from: partOfDay ? hours.from : null,
          to: partOfDay ? hours.to : null,
          reason,
        },
      ]),
    );
    setSelection(null);
  };

  const remove = (entry) =>
    onChange(
      withTimeOff(
        resource.monthlySchedule,
        entries.filter((existing) => !sameEntry(existing, entry)),
      ),
    );

  const describe = (entry) =>
    [
      t(`Dashboard.TimeOff.reasons.${entry.reason || "dayOff"}`),
      entry.from
        ? `${entry.from} – ${entry.to}`
        : t("Dashboard.TimeOff.allDay"),
    ].join(" · ");

  const scheduled = upcoming(entries, todayIso);
  const overlapping = selection
    ? scheduled.filter(
        (entry) => entry.start <= selection.end && entry.end >= selection.start,
      )
    : [];

  const firstDay = new Date(view.year, view.month, 1);
  const leadingBlanks = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const hoursErrorId = `${idPrefix}-hours-error`;

  const navClass =
    "flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-ink-200 bg-white text-ink-600 transition hover:border-brand-600 hover:text-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-xs text-ink-500">{t("Dashboard.TimeOff.help")}</p>

      {/* The month keeps a calendar's proportions on a wide page rather than
          stretching each day into a broad tile. */}
      <div className="flex w-full max-w-md flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            className={navClass}
            onClick={() => page(-1)}
            disabled={monthsAhead <= 0}
            aria-label={t("Dashboard.TimeOff.previousMonth")}
          >
            <i className="bi bi-chevron-left" aria-hidden="true" />
          </button>
          <h5
            className="m-0 text-sm font-semibold text-ink-900"
            aria-live="polite"
            id={`${idPrefix}-month`}
          >
            {text.monthTitle(view.year, view.month)}
          </h5>
          <button
            type="button"
            className={navClass}
            onClick={() => page(1)}
            disabled={monthsAhead >= MAX_MONTHS_AHEAD}
            aria-label={t("Dashboard.TimeOff.nextMonth")}
          >
            <i className="bi bi-chevron-right" aria-hidden="true" />
          </button>
        </div>

        <div role="group" aria-labelledby={`${idPrefix}-month`}>
          <div className="grid grid-cols-7 gap-1" aria-hidden="true">
            {DAY_KEYS.map((day) => (
              <span
                key={day}
                className="py-1 text-center text-[0.6875rem] font-semibold text-ink-400"
              >
                {t(`Dashboard.Setup.days.short.${day}`)}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: leadingBlanks }).map((_, index) => (
              <span key={`blank-${index}`} />
            ))}
            {Array.from({ length: daysInMonth }).map((_, index) => {
              const date = new Date(view.year, view.month, index + 1);
              const iso = toIsoDate(date);
              const past = iso < todayIso;
              const on = entriesOn(entries, iso);
              const wholeDay = on.find((entry) => !entry.from);
              const partial = on.find((entry) => entry.from);
              const selected =
                Boolean(selection) &&
                selection.start <= iso &&
                iso <= selection.end;
              const edge =
                Boolean(selection) &&
                (iso === selection.start || iso === selection.end);
              const working = worksOn(
                resource,
                DAY_KEYS[(date.getDay() + 6) % 7],
              );

              const look = edge
                ? "border-brand-600 bg-brand-600 text-white"
                : selected
                  ? "border-brand-300 bg-brand-100 text-brand-900"
                  : wholeDay
                    ? styleOf(wholeDay.reason).day
                    : past
                      ? "border-transparent text-ink-300"
                      : working
                        ? "border-ink-200 bg-white text-ink-700 hover:border-brand-600 hover:bg-brand-50"
                        : "border-transparent bg-ink-50 text-ink-400 hover:border-brand-300";

              return (
                <button
                  key={iso}
                  type="button"
                  disabled={past}
                  aria-pressed={selected}
                  aria-label={[text.dayName(iso), ...on.map(describe)].join(
                    ", ",
                  )}
                  onClick={() => choose(iso)}
                  className={`relative flex h-10 cursor-pointer items-center justify-center rounded-md border text-xs font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:cursor-default ${look} ${
                    past && wholeDay ? "opacity-50" : ""
                  } ${iso === todayIso ? "ring-2 ring-brand-600 ring-offset-1" : ""}`}
                >
                  {index + 1}
                  {/* A break on a day the weekly hours leave free changes
                    nothing, so it is not marked there. */}
                  {partial && !wholeDay && working && (
                    <span
                      aria-hidden="true"
                      className={`absolute bottom-1 size-1.5 rounded-full ${
                        edge ? "bg-white" : styleOf(partial.reason).dot
                      }`}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <ul
          className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-xs text-ink-500"
          aria-label={t("Dashboard.TimeOff.legend")}
        >
          {TIME_OFF_REASONS.map((value) => (
            <li key={value} className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className={`size-2.5 rounded-full ${styleOf(value).dot}`}
              />
              {t(`Dashboard.TimeOff.reasons.${value}`)}
            </li>
          ))}
        </ul>
      </div>

      {selection && (
        <div
          className="rounded-lg border border-brand-200 bg-brand-50/60 p-3"
          data-testid="time-off-panel"
        >
          <p className="m-0 text-sm font-semibold text-ink-900">
            {text.range(selection)}{" "}
            <span className="font-normal text-ink-500">
              · {t("Dashboard.TimeOff.days", { count: daysIn(selection) })}
            </span>
          </p>
          {selection.start === selection.end && (
            <p className="m-0 mt-0.5 text-xs text-ink-500">
              {t("Dashboard.TimeOff.pickEnd")}
            </p>
          )}

          <div
            role="group"
            aria-label={t("Dashboard.TimeOff.reasonLabel")}
            className="mt-3 flex flex-wrap gap-2"
          >
            {TIME_OFF_REASONS.map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={reason === value}
                onClick={() => chooseReason(value)}
                className={chipClass(reason === value)}
              >
                <span
                  aria-hidden="true"
                  className={`size-2.5 rounded-full ${styleOf(value).dot}`}
                />
                {t(`Dashboard.TimeOff.reasons.${value}`)}
              </button>
            ))}
          </div>

          <div
            role="group"
            aria-label={t("Dashboard.TimeOff.lengthLabel")}
            className="mt-2 flex flex-wrap gap-2"
          >
            {[false, true].map((part) => (
              <button
                key={String(part)}
                type="button"
                aria-pressed={partOfDay === part}
                onClick={() => setPartOfDay(part)}
                className={chipClass(partOfDay === part)}
              >
                {t(
                  part
                    ? "Dashboard.TimeOff.someHours"
                    : "Dashboard.TimeOff.allDay",
                )}
              </button>
            ))}
          </div>

          {partOfDay && (
            <div className="mt-3 grid max-w-sm grid-cols-2 gap-3">
              <div className="min-w-0">
                <label
                  htmlFor={`${idPrefix}-from`}
                  className="mb-1.5 block text-xs font-semibold text-ink-600"
                >
                  {t("Dashboard.TimeOff.from")}
                </label>
                <input
                  type="time"
                  id={`${idPrefix}-from`}
                  value={hours.from}
                  onChange={(e) =>
                    setHours((prev) => ({ ...prev, from: e.target.value }))
                  }
                  className={formControlClass}
                />
              </div>
              <div className="min-w-0">
                <label
                  htmlFor={`${idPrefix}-to`}
                  className="mb-1.5 block text-xs font-semibold text-ink-600"
                >
                  {t("Dashboard.TimeOff.to")}
                </label>
                <input
                  type="time"
                  id={`${idPrefix}-to`}
                  value={hours.to}
                  onChange={(e) =>
                    setHours((prev) => ({ ...prev, to: e.target.value }))
                  }
                  className={
                    hoursInvalid ? formControlErrorClass : formControlClass
                  }
                  aria-invalid={hoursInvalid ? "true" : undefined}
                  aria-describedby={hoursInvalid ? hoursErrorId : undefined}
                />
              </div>
              {hoursInvalid && (
                <span
                  id={hoursErrorId}
                  className={`${formErrorTextClass} col-span-2`}
                >
                  {t("Dashboard.Wizard.Steps.validation.resourceHoursInvalid")}
                </span>
              )}
            </div>
          )}

          {overlapping.length > 0 && (
            <div className="mt-3">
              <p className="m-0 text-xs font-semibold text-ink-600">
                {t("Dashboard.TimeOff.alreadyScheduled")}
              </p>
              <ul className="m-0 mt-1 flex list-none flex-col gap-1 p-0">
                {overlapping.map((entry) => (
                  <TimeOffRow
                    key={`${entry.start}-${entry.end}-${entry.from}-${entry.reason}`}
                    entry={entry}
                    rangeText={text.range(entry)}
                    detail={describe(entry)}
                    onRemove={() => remove(entry)}
                    removeLabel={t("Dashboard.TimeOff.remove", {
                      label: text.range(entry),
                    })}
                  />
                ))}
              </ul>
            </div>
          )}

          {tooLong && (
            <p className={formErrorTextClass} role="alert">
              {t("Dashboard.TimeOff.tooLong")}
            </p>
          )}
          {atCap && (
            <p className={formErrorTextClass} role="alert">
              {t("Dashboard.TimeOff.tooMany")}
            </p>
          )}

          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => setSelection(null)}
              className="inline-flex min-h-11 cursor-pointer items-center rounded-lg px-4 text-sm font-semibold text-ink-600 transition hover:bg-ink-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
            >
              {t("Dashboard.TimeOff.cancel")}
            </button>
            <button
              type="button"
              onClick={add}
              disabled={!canAdd}
              className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border border-brand-600 bg-brand-600 px-4 text-sm font-semibold text-white transition hover:bg-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <i className="bi bi-plus-lg" aria-hidden="true" />
              {t("Dashboard.TimeOff.add")}
            </button>
          </div>
        </div>
      )}

      <div>
        <p className="m-0 text-xs font-semibold text-ink-600">
          {t("Dashboard.TimeOff.listTitle")}
        </p>
        {scheduled.length === 0 ? (
          <p className="m-0 mt-1 text-xs text-ink-400">
            {t("Dashboard.TimeOff.empty")}
          </p>
        ) : (
          <ul
            className="m-0 mt-1 flex list-none flex-col gap-1 p-0"
            data-testid="time-off-list"
          >
            {scheduled.map((entry) => (
              <TimeOffRow
                key={`${entry.start}-${entry.end}-${entry.from}-${entry.reason}`}
                entry={entry}
                rangeText={text.range(entry)}
                detail={describe(entry)}
                onRemove={() => remove(entry)}
                removeLabel={t("Dashboard.TimeOff.remove", {
                  label: text.range(entry),
                })}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/** One scheduled period, with the bin that removes it. */
const TimeOffRow = ({ entry, rangeText, detail, onRemove, removeLabel }) => (
  <li className="flex items-center gap-2 rounded-lg border border-ink-200 bg-white py-1 pr-1 pl-3">
    <span
      aria-hidden="true"
      className={`size-2.5 shrink-0 rounded-full ${styleOf(entry.reason).dot}`}
    />
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-medium break-words text-ink-900">
        {rangeText}
      </span>
      <span className="block text-xs text-ink-500">{detail}</span>
    </span>
    <button
      type="button"
      onClick={onRemove}
      aria-label={removeLabel}
      className="inline-flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-lg text-ink-500 transition hover:bg-red-50 hover:text-red-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
    >
      <i className="bi bi-trash" aria-hidden="true" />
    </button>
  </li>
);

TimeOffRow.propTypes = {
  entry: PropTypes.object.isRequired,
  rangeText: PropTypes.string.isRequired,
  detail: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired,
  removeLabel: PropTypes.string.isRequired,
};

TimeOffEditor.propTypes = {
  /** The resource — its `monthlySchedule` is edited, its weekly hours shade the calendar. */
  resource: PropTypes.object.isRequired,
  /** Called with the whole new `monthlySchedule`. */
  onChange: PropTypes.func.isRequired,
  /** Keeps ids unique when several editors are on one page. */
  idPrefix: PropTypes.string,
};

export default TimeOffEditor;
